import { useCallback, useEffect, useRef, useState } from 'react'
import { useAuth } from '../auth/AuthContext'

interface UseAutoSaveOptions {
  documentId: string
  onSave: (content: string) => Promise<void>
  delay?: number // デバウンス遅延時間（ミリ秒）
  enabled?: boolean
}

export function useAutoSave({ documentId, onSave, delay = 2000, enabled = true }: UseAutoSaveOptions) {
  const [isSaving, setIsSaving] = useState(false)
  const [lastSaved, setLastSaved] = useState<Date | null>(null)
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)
  
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)
  const lastContentRef = useRef<string>('')
  const { firebaseUser } = useAuth()

  const save = useCallback(async (content: string) => {
    if (!enabled || !firebaseUser || content === lastContentRef.current) {
      return
    }

    try {
      setIsSaving(true)
      setSaveError(null)
      
      await onSave(content)
      
      lastContentRef.current = content
      setLastSaved(new Date())
      setHasUnsavedChanges(false)
      
      console.log('✅ Auto-save completed:', { documentId, timestamp: new Date().toISOString() })
    } catch (error) {
      console.error('❌ Auto-save failed:', error)
      setSaveError(error instanceof Error ? error.message : '保存に失敗しました')
    } finally {
      setIsSaving(false)
    }
  }, [documentId, onSave, enabled, firebaseUser])

  const scheduleAutoSave = useCallback((content: string) => {
    if (!enabled) return

    // 既存のタイマーをクリア
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }

    // 内容が変更されている場合のみ保存をスケジュール
    if (content !== lastContentRef.current) {
      setHasUnsavedChanges(true)
      
      timeoutRef.current = setTimeout(() => {
        save(content)
      }, delay)
    }
  }, [save, delay, enabled])

  const forceSave = useCallback(async (content: string) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }
    await save(content)
  }, [save])

  // コンポーネントのアンマウント時にタイマーをクリア
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [])

  return {
    isSaving,
    lastSaved,
    hasUnsavedChanges,
    saveError,
    scheduleAutoSave,
    forceSave
  }
}
